/**
 * IMPORTANT: Please update the e2e tests if you make any changes to the login page
 */

import * as React from 'react';
import * as cn from 'classnames';
import { LoadingAnimation } from '@mq/aurora-components/dist/loading';
import { DEVELOPER_SITE_PREFIX, MARQUEE_SITE_PREFIX } from '@mq/aurora-launcher-core';
import {
  AuroraLogin,
  getDecodedFromUrl,
  getHostname,
  getQueryParams,
  GSAuthSystem
} from '@mq/aurora-login';
import { requestIsOrbitBrowser } from '../../APIs/is-orbit';
import * as heroStyle from '../../styles/hero.css';
import { IBaseProps } from '../../types';
import { isWebPSupported } from '../../utils/webp-supported';
import { Footer } from '../footer/footer';
import { HeaderMobile } from '../header-mobile/header-mobile';
import { Header } from '../header/header';
import {
  FOOTER_LINKS,
  HEADER_LINKS,
  IBD_AUCTIONS_FOOTER_LINKS,
  IBD_AUCTIONS_HEADER_LINKS,
  IBD_AUCTIONS_URL_PATH,
  ILink
} from './login-page-links';
import { clearUsername, getUsername, setUsername } from './login-page-storage';
import * as styles from './login-page.css';
import { IbdEcmInvestorContact } from './ibd-ecm-investor-contact';

interface ILoginPageProps extends IBaseProps {
  history: {
    push: (url: string) => void;
  };
  location?: { search?: string };
}

interface ILoginPageState {
  isOrbitBrowser: boolean | undefined;
  isDoneTryingSSO: boolean;
}

const PRIME_PATH_REGEX = /^.*\/s\/prime.*$/;
const GS360_PATH_REGEX = /^.*360(-qa)?\.gs\.com.*$/;

export class LoginPage extends React.Component<ILoginPageProps, ILoginPageState> {
  didUnmount = false;
  isIBDAuctionsRedirect = false;
  allowPasswordFor2FA = false;

  state: ILoginPageState = {
    isOrbitBrowser: undefined,
    isDoneTryingSSO: false
  };

  constructor(props: ILoginPageProps) {
    super(props);
    this.checkRedirectQueryParameter();
  }

  async componentDidMount(): Promise<void> {
    this.didUnmount = false;
    const isOrbitBrowser = await requestIsOrbitBrowser();
    if (!this.didUnmount) {
      this.setState({ isOrbitBrowser });
    }
  }

  componentWillUnmount() {
    this.didUnmount = true;
  }

  getSavedUsername = () => {
    return getUsername();
  };

  setSavedUsername = (username?: string) => {
    if (username) {
      setUsername(username);
    } else {
      clearUsername();
    }
  };

  /**
   * Checks the From query param and sets variables accordingly
   *
   * Sets isIBDAuctionsRedirect to true if redirected from the IBD ECM Investor
   * page (on authenticatication failure).
   *
   * Sets allowPasswordFor2FA to true if redirected from a prime feature
   */
  checkRedirectQueryParameter(): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const fromUrl: string = getQueryParams(this.props.location?.search || '')?.From;
    if (fromUrl) {
      const decoded = getDecodedFromUrl(fromUrl) || '';
      if (decoded && decoded.indexOf(IBD_AUCTIONS_URL_PATH) > -1) {
        this.isIBDAuctionsRedirect = true;
      } else if (decoded && (PRIME_PATH_REGEX.test(decoded) || GS360_PATH_REGEX.test(decoded))) {
        this.allowPasswordFor2FA = true;
      }
    }
  }

  getHeaderLinks(): ILink[] {
    return this.isIBDAuctionsRedirect ? IBD_AUCTIONS_HEADER_LINKS : HEADER_LINKS;
  }

  getFooterLinks(): ILink[] {
    return this.isIBDAuctionsRedirect
      ? IBD_AUCTIONS_FOOTER_LINKS.map(link => {
          return { title: link.title, url: `${location.protocol}//${location.host}${link.url}` };
        })
      : FOOTER_LINKS;
  }

  getDeliveredByText(): string {
    return this.isIBDAuctionsRedirect
      ? ''
      : 'The future of institutional finance, delivered by Goldman Sachs';
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  onLoginReady = async (): Promise<void> => {
    this.setState({ isDoneTryingSSO: true });
  };

  render() {
    const DEVELOPER_SITE_URL = 'developer.gs.com';
    const {
      match,
      history: { push }
    } = this.props;

    const { isOrbitBrowser, isDoneTryingSSO } = this.state;

    const style: React.CSSProperties = {
      backgroundImage: `url('/resources/hero-home${isWebPSupported() ? '.webp' : '.png'}')`,
      flex: 1,
      flexShrink: 1,
      flexBasis: 'auto'
    };

    let onLoginSuccessDefaultPath = MARQUEE_SITE_PREFIX;
    const currentHostname = getHostname();
    if (currentHostname && currentHostname.indexOf(DEVELOPER_SITE_URL) !== -1) {
      onLoginSuccessDefaultPath = DEVELOPER_SITE_PREFIX;
    }

    return (
      <div className={heroStyle.rootLevel}>
        <Header
          match={match}
          push={push}
          links={this.getHeaderLinks()}
          disableGsLogoClick={this.isIBDAuctionsRedirect}
        />

        {!this.isIBDAuctionsRedirect && (
          <HeaderMobile match={match} links={this.getHeaderLinks()} />
        )}

        <div className={cn(heroStyle.hero, heroStyle.cover, 'grd-row', styles.login)} style={style}>
          <div className="grd-cell-lg-2" />
          <div className={`${styles.left} grd-cell-12 grd-cell-md-6 grd-cell-lg-4`}>
            {!this.isIBDAuctionsRedirect && (
              <div className={`${heroStyle.content} aurora-login-delivered-by`}>
                <h1>
                  {this.getDeliveredByText()}
                  <span className={heroStyle.reg}>&reg;</span>
                </h1>
              </div>
            )}
          </div>
          <div className={cn(heroStyle.content, 'grd-cell-12', 'grd-cell-md-6', 'grd-cell-lg-6')}>
            {isOrbitBrowser !== undefined && (
              <>
                <div
                  className={cn({
                    [styles.loginLoaderHidden]: isDoneTryingSSO,
                    [styles.loginLoader]: !isDoneTryingSSO
                  })}
                >
                  <LoadingAnimation loaderSize="10px" />
                </div>
                <AuroraLogin
                  className={cn({ [styles.loginLoaderHidden]: !isDoneTryingSSO })}
                  authSystem={GSAuthSystem.PING}
                  loginBasePath={match.path}
                  enableRememberUsernameFeature
                  getSavedUsername={this.getSavedUsername}
                  onLoginReady={this.onLoginReady}
                  onLoginSuccessDefaultPath={onLoginSuccessDefaultPath}
                  setSavedUsername={this.setSavedUsername}
                  disableFooter={this.isIBDAuctionsRedirect}
                  allowPasswordFor2FA={this.allowPasswordFor2FA}
                />
                {this.isIBDAuctionsRedirect && <IbdEcmInvestorContact />}
              </>
            )}
          </div>
        </div>
        <Footer links={this.getFooterLinks()} disableGsLogoClick={this.isIBDAuctionsRedirect} />
      </div>
    );
  }
}
